import React, { useState, useCallback } from "react"

import Input from "../../components/input"
import { useAuth } from "../../hooks/auth"
import useFlash from "../../hooks/use_flash"
import api, { ApiFieldError } from "../../../services/api"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"

const Card = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <div className="flex gap-12">
    <div className="w-40 font-medium text-right">{title}</div>
    <div>{children}</div>
  </div>
)

const ChangeEmailCard = () => {
  const { userEmail, retry } = useAuth()
  const { setFlash } = useFlash()

  const [email, setEmail] = useState("")
  const [passwordForEmailChange, setPasswordForEmailChange] =
    useState<string>("")
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordForEmailChangeError, setPasswordForEmailChangeError] =
    useState<string | null>(null)
  const [inProgress, setInProgress] = useState(false)

  const submit = () => {
    setInProgress(true)
    api()
      .put("/api/me", {
        user: { email: email },
        current_password: passwordForEmailChange,
      })
      .then(({ data }) => {
        setEmailError(null)
        setEmail("")
        setPasswordForEmailChange("")
        setPasswordForEmailChangeError(null)
        setInProgress(false)
        setFlash({
          type: "success",
          text: "Email changed successfully.",
        })
        retry()
      })
      .catch(({ response }) => {
        const { data } = response
        if (data.status === "error") {
          data.errors.forEach((error: ApiFieldError) => {
            if (error.field == "email") setEmailError(error.message)
            if (error.field == "current_password")
              setPasswordForEmailChangeError(error.message)
          })
        }
        setInProgress(false)
      })
  }

  return (
    <Card title="Change email">
      <div>
        <div className="flex gap-4">
          <Input
            label="New email"
            name="email"
            type="email"
            value={email}
            placeholder={userEmail || "New email"}
            onChange={(v: string) => setEmail(v)}
            onBlur={() => {}}
            disabled={inProgress}
            error={emailError}
          />
          <Input
            label="Confirm password"
            name="password"
            type="password"
            id="password-for-email-change"
            value={passwordForEmailChange}
            onChange={(v: string) => setPasswordForEmailChange(v)}
            onBlur={() => {}}
            disabled={inProgress}
            error={passwordForEmailChangeError}
          />
        </div>
        {inProgress ? (
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="text-gray-500 mt-6"
          />
        ) : (
          <button
            className="mt-6 rounded p-2 px-3 bg-sky-50 dark:bg-sky-700 font-medium ring-1 ring-gray-100 text-sm hover:bg-sky-100"
            onClick={() => submit()}
            disabled={inProgress}
          >
            Change email
          </button>
        )}
      </div>
    </Card>
  )
}

const ChangePasswordCard = () => {
  const { setFlash } = useFlash()

  const [password, setPassword] = useState("")
  const [passwordForPasswordChange, setPasswordForPasswordChange] =
    useState<string>("")
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordForPasswordChangeError, setPasswordForPasswordChangeError] =
    useState<string | null>(null)
  const [inProgress, setInProgress] = useState(false)

  const submit = () => {
    setInProgress(true)
    api()
      .put("/api/me", {
        user: { password: password },
        current_password: passwordForPasswordChange,
      })
      .then(() => {
        setPassword("")
        setPasswordError(null)
        setPasswordForPasswordChange("")
        setPasswordForPasswordChangeError(null)
        setInProgress(false)
        setFlash({
          type: "success",
          text: "Password changed successfully.",
        })
      })
      .catch(({ response }) => {
        const { data } = response
        if (data.status === "error") {
          data.errors.forEach((error: ApiFieldError) => {
            if (error.field == "password") setPasswordError(error.message)
            if (error.field == "current_password")
              setPasswordForPasswordChangeError(error.message)
          })
        }
        setInProgress(false)
      })
  }

  return (
    <Card title="Change password">
      <div>
        <div className="flex gap-4">
          <Input
            label="New password"
            name="new_password"
            type="password"
            value={password}
            onChange={(v: string) => setPassword(v)}
            onBlur={() => {}}
            disabled={inProgress}
            error={passwordError}
          />
          <Input
            label="Confirm existing password"
            name="password"
            type="password"
            id="password-for-password-change"
            value={passwordForPasswordChange}
            onChange={(v: string) => setPasswordForPasswordChange(v)}
            onBlur={() => {}}
            disabled={inProgress}
            error={passwordForPasswordChangeError}
          />
        </div>
        {inProgress ? (
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="text-gray-500 mt-6"
          />
        ) : (
          <button
            className="mt-6 rounded p-2 px-3 bg-sky-50 dark:bg-sky-700 font-medium ring-1 ring-gray-100 text-sm hover:bg-sky-100"
            onClick={() => submit()}
            disabled={inProgress}
          >
            Change password
          </button>
        )}
      </div>
    </Card>
  )
}

const DeleteAccountCard = () => {
  const deleteAccount = () => {
    if (
      confirm(
        "This will delete ALL your data in Nana, permanently. Are you sure? "
      )
    ) {
      api()
        .delete("/api/me")
        .then(() => {
          window.location.pathname = "/"
        })
    }
  }

  return (
    <Card title="Delete account">
      <div className="text-sm">To delete your account, click here:</div>

      <button
        className="mt-4 px-2 py-1 rounded ring-1 ring-red-500 text-red-500 bg-gray-100 dark:bg-gray-700 font-bold text-xs hover:bg-red-300"
        onClick={deleteAccount}
      >
        Delete account
      </button>

      <div className="mt-2 text-sm">Be careful, this cannot be undone.</div>
    </Card>
  )
}

const NewsletterPreferenceCard = () => {
  const { newsletterAccepted, retry } = useAuth()
  const { setFlash } = useFlash()
  const [newsletterAcceptedState, setNewsletterAcceptedState] =
    useState<boolean>(!!newsletterAccepted)
  const [inProgress, setInProgress] = useState<boolean>(false)

  const clearSpinnerAfterDelay = (start: Date) => {
    setTimeout(() => {
      setInProgress(false)
    }, Math.max(0, 500 - (new Date().getTime() - start.getTime())))
  }

  const update = (isChecked: boolean) => {
    setInProgress(true)
    const start = new Date()
    api()
      .put("/api/me", {
        user: { newsletter_accepted: isChecked },
      })
      .then(() => {
        clearSpinnerAfterDelay(start)
        retry()
      })
      .catch(() => {
        clearSpinnerAfterDelay(start)
        setFlash({
          type: "error",
          text: "There was a problem changing your newsletter preference, please contact support.",
        })
      })
  }

  return (
    <Card title="Newsletter">
      {inProgress ? (
        <FontAwesomeIcon icon={faSpinner} spin className="text-gray-500" />
      ) : (
        <input
          type="checkbox"
          id="newsletter-acceptance"
          data-testid="newsletter-checkbox"
          checked={newsletterAcceptedState}
          onChange={(e) => {
            setNewsletterAcceptedState(e.target.checked)
            update(e.target.checked)
          }}
        />
      )}
      <label
        className="ml-2 cursor-pointer text-sm"
        htmlFor="newsletter-acceptance"
      >
        Send me updates about Nana by email, max once a week.
      </label>
    </Card>
  )
}

const AdminCard = () => {
  const { isAdmin } = useAuth()
  if (!isAdmin) return null

  return (
    <Card title="Admin section">
      <div>Account is admin</div>
    </Card>
  )
}

export default function AccountPage() {
  return (
    <div className="ml-12 flex min-h-0 flex-auto flex-col items-start gap-8 overflow-y-scroll p-8 pt-24 dark:bg-gray-950">
      <h1 className="text-2xl ml-52 font-medium">Account</h1>
      <ChangeEmailCard />
      <ChangePasswordCard />
      <NewsletterPreferenceCard />
      <Card title="Export account">
        <div className="text-sm">
          To export your data, contact{" "}
          <a
            className="dark:text-sky-400 text-sky-800 underline"
            href="mailto:support@nanamap.com"
          >
            support.
          </a>
        </div>
      </Card>
      <DeleteAccountCard />
      <AdminCard />
    </div>
  )
}
